import { useState, useEffect } from 'react';
import { SearchForm } from './SearchForm';
import { SearchFilters } from './SearchFilters';
import { SearchResults } from './SearchResults';
import { SearchResultsHeader } from './SearchResultsHeader';

interface AutocompleteSuggestion {
    id: number;
    name: string;
    url: string;
    contentType: string;
}

interface SearchResult {
    id: number;
    name: string;
    url: string;
    metaDescription?: string;
    contentType: string;
    createdDate: string;
    categories: string[];
    pageType?: string;
}

interface SearchResponse {
    query: string;
    results: SearchResult[];
    pagination: {
        page: number;
        pageSize: number;
        totalResults: number;
        totalPages: number;
    };
}

interface SearchAppProps {
    apiUrl?: string;
    autocompleteApiUrl?: string;
    initialQuery?: string;
    placeholder?: string;
    buttonText?: string;
    debounceMs?: number;
}

export const SearchApp: React.FC<SearchAppProps> = ({
    apiUrl = '/umbraco/api/SearchApi/Search',
    autocompleteApiUrl = '/umbraco/api/SearchApi/Autocomplete',
    initialQuery = '',
    placeholder = 'Search NACUFS...',
    buttonText = 'Search',
    debounceMs = 300
}) => {
    // Client-side pagination page size
    const CLIENT_PAGE_SIZE = 10;
    
    const [query, setQuery] = useState(initialQuery);
    const [results, setResults] = useState<SearchResult[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [totalResults, setTotalResults] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [selectedPageTypes, setSelectedPageTypes] = useState<string[]>([]);
    const [allResults, setAllResults] = useState<SearchResult[]>([]);
    const [sortBy, setSortBy] = useState<string>('newest');

    // Sort results
    const sortResults = (resultsToSort: SearchResult[]) => {
        return [...resultsToSort].sort((a, b) => {
            const dateA = new Date(a.createdDate);
            const dateB = new Date(b.createdDate);
            
            if (sortBy === 'newest') {
                return dateB.getTime() - dateA.getTime(); // Newest first
            } else if (sortBy === 'oldest') {
                return dateA.getTime() - dateB.getTime(); // Oldest first
            } else {
                return 0; // Default to no sorting for relevance
            }
        });
    };
    
    // Filter results based on selected filters
    const applyFilters = (resultsToFilter: SearchResult[]) => {
        const filtered = resultsToFilter.filter(result => {
            // Filter by categories
            const matchesCategory = selectedCategories.length === 0 || 
                selectedCategories.some(category => result.categories.includes(category));
            
            // Filter by page type
            const matchesPageType = selectedPageTypes.length === 0 || 
                (result.pageType && selectedPageTypes.includes(result.pageType));
            
            return matchesCategory && matchesPageType;
        });
        
        // Apply sorting after filtering
        return sortResults(filtered);
    };    
    
    // Handle filter changes
    const handleCategoryFilter = (category: string, checked: boolean) => {
        const newCategories = checked 
            ? [...selectedCategories, category]
            : selectedCategories.filter(c => c !== category);
            
        setSelectedCategories(newCategories);
        setCurrentPage(1); // Reset to first page when filtering
        
        // Update URL with new filters
        updateUrlQueryString(query, newCategories, selectedPageTypes);
        
        // Check if this will be the last filter being removed and no search query exists
        const willHaveNoFilters = newCategories.length === 0 && selectedPageTypes.length === 0;
        const hasNoSearchQuery = !query.trim();
        
        if (!checked && willHaveNoFilters && hasNoSearchQuery) {
            // Unchecking the last filter with no search query - return to initial state
            setResults([]);
            setAllResults([]);
            setHasSearched(false);
            setTotalResults(0);
            setTotalPages(0);
            setCurrentPage(1);
        } else if (!hasSearched && (checked || newCategories.length > 0 || selectedPageTypes.length > 0)) {
            // If no search has been performed yet and we're adding a filter, perform a wildcard search
            performSearch('*', 1);
        }
    };
    
    const handlePageTypeFilter = (pageType: string, checked: boolean) => {
        const newPageTypes = checked 
            ? [...selectedPageTypes, pageType]
            : selectedPageTypes.filter(pt => pt !== pageType);
            
        setSelectedPageTypes(newPageTypes);
        setCurrentPage(1); // Reset to first page when filtering
        
        // Update URL with new filters
        updateUrlQueryString(query, selectedCategories, newPageTypes);
        
        // Check if this will be the last filter being removed and no search query exists
        const willHaveNoFilters = selectedCategories.length === 0 && newPageTypes.length === 0;
        const hasNoSearchQuery = !query.trim();
        
        if (!checked && willHaveNoFilters && hasNoSearchQuery) {
            // Unchecking the last filter with no search query - return to initial state
            setResults([]);
            setAllResults([]);
            setHasSearched(false);
            setTotalResults(0);
            setTotalPages(0);
            setCurrentPage(1);
        } else if (!hasSearched && (checked || selectedCategories.length > 0 || newPageTypes.length > 0)) {
            // If no search has been performed yet and we're adding a filter, perform a wildcard search
            performSearch('*', 1);
        }
    };    
    
    const handleResetFilters = () => {
        setSelectedCategories([]);
        setSelectedPageTypes([]);
        setCurrentPage(1);
        
        // Update URL to remove filters
        updateUrlQueryString(query, [], []);
        
        // If we've searched before or had filters applied, and there's no search query, reset to initial state
        if (hasSearched && !query.trim()) {
            setResults([]);
            setAllResults([]);
            setHasSearched(false);
            setTotalResults(0);
            setTotalPages(0);
            setCurrentPage(1);
        }
    };
    
    const handleSortChange = (newSortBy: string) => {
        setSortBy(newSortBy);
        setCurrentPage(1); // Reset to first page when sorting changes
    };

    const performSearch = async (searchQuery: string, page: number = 1) => {
        const isWildcardSearch = searchQuery === '*';
        
        if (!searchQuery.trim() && !isWildcardSearch) {
            setResults([]);
            setAllResults([]);
            setHasSearched(false);
            setTotalResults(0);
            setTotalPages(0);
            setCurrentPage(1);
            return;
        }

        setLoading(true);
        setError(null);
        setHasSearched(true);     

        try {
            // For client-side filtering, we need to fetch all results (use a large page size)
            const queryParam = isWildcardSearch ? '*' : encodeURIComponent(searchQuery);
            const response = await fetch(`${apiUrl}?query=${queryParam}&page=1&pageSize=1000`);
            
            if (!response.ok) {
                throw new Error(`Search failed: ${response.status}`);
            }

            const data: SearchResponse = await response.json();
            // Filter out items without valid names
            const validResults = data.results.filter(result => 
                result.name && result.name.trim().length > 0
            );
            setAllResults(validResults);

            const filteredResults = applyFilters(validResults);
            setResults(filteredResults);
            setTotalResults(filteredResults.length);
            setCurrentPage(1); // Always start from page 1 for new searches
            setTotalPages(Math.ceil(filteredResults.length / CLIENT_PAGE_SIZE));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred while searching');
            setResults([]);
            setAllResults([]);
            setTotalResults(0);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }        
    };

    // Handle page changes
    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages && page !== currentPage) {
            // Temporarily disable navigation slide-down animation
            const navContainer = document.querySelector('[data-main-nav-container]');
            if (navContainer) {
                navContainer.setAttribute('data-skip-slide-down', 'true');
            }
            
            setCurrentPage(page);
            // Scroll to top of the page when changing pages
            window.scrollTo({ top: 0, behavior: 'smooth' });
            
            // Re-enable navigation animation after a brief delay
            setTimeout(() => {
                if (navContainer) {
                    navContainer.removeAttribute('data-skip-slide-down');
                }
            }, 0);
        }
    };

    // Get paginated results for display
    const getPaginatedResults = () => {
        const startIndex = (currentPage - 1) * CLIENT_PAGE_SIZE;
        const endIndex = startIndex + CLIENT_PAGE_SIZE;
        return results.slice(startIndex, endIndex);
    };    

    // Effect to reapply filters and sorting when filter state or sort changes
    useEffect(() => {
        if (allResults.length > 0) {
            const filteredResults = applyFilters(allResults);
            setResults(filteredResults);
            setTotalResults(filteredResults.length);
            setTotalPages(Math.ceil(filteredResults.length / CLIENT_PAGE_SIZE));
            setCurrentPage(1); // Reset to first page
        }
    }, [selectedCategories, selectedPageTypes, allResults, sortBy]);    
    
    // Effect to scroll to top when current page changes
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentPage]);
    
    // Initialize with URL query parameter and filters and perform initial search if needed
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const urlQuery = urlParams.get('query');
        const urlCategories = urlParams.get('categories');
        const urlPageTypes = urlParams.get('pageTypes');
        
        if (urlQuery) {
            setQuery(urlQuery);
        }
        
        if (urlCategories) {
            const categories = urlCategories.split(',').filter(c => c.trim());
            setSelectedCategories(categories);
        }
        
        if (urlPageTypes) {
            const pageTypes = urlPageTypes.split(',').filter(pt => pt.trim());
            setSelectedPageTypes(pageTypes);
        }
        
        // Perform search if there's a query, or if there are filters but no query (show all results with filters applied)
        if (urlQuery) {
            performSearch(urlQuery, 1); // Perform search with query
        } else if (urlCategories || urlPageTypes) {
            // No query but filters present - search with empty query to get all results, then apply filters
            performSearch('*', 1); // Use wildcard to get all results
        }
    }, []);    
    
    // Function to update URL query string with search and filters
    const updateUrlQueryString = (searchQuery: string, categories?: string[], pageTypes?: string[]) => {
        const url = new URL(window.location.href);
        
        // Don't include wildcard queries in the URL
        if (searchQuery.trim() && searchQuery !== '*') {
            url.searchParams.set('query', searchQuery.trim());
        } else {
            url.searchParams.delete('query');
        }
        
        // Use provided arrays or current state
        const categoriesToUse = categories !== undefined ? categories : selectedCategories;
        const pageTypesToUse = pageTypes !== undefined ? pageTypes : selectedPageTypes;
        
        if (categoriesToUse.length > 0) {
            url.searchParams.set('categories', categoriesToUse.join(','));
        } else {
            url.searchParams.delete('categories');
        }
        
        if (pageTypesToUse.length > 0) {
            url.searchParams.set('pageTypes', pageTypesToUse.join(','));
        } else {
            url.searchParams.delete('pageTypes');
        }
        
        // Update the URL without triggering a page reload
        window.history.pushState(null, '', url.toString());
    };
    
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        updateUrlQueryString(query, selectedCategories, selectedPageTypes);
        performSearch(query, 1); // Start from page 1 for manual search
    };

    const handleSuggestionSelect = (suggestion: AutocompleteSuggestion) => {
        // When a suggestion is selected, update the URL and perform a search with that query
        setQuery(suggestion.name);
        updateUrlQueryString(suggestion.name, selectedCategories, selectedPageTypes);
        performSearch(suggestion.name, 1);
    };

    return (
        <div className="react-search-app">
            <SearchForm
                query={query}
                placeholder={placeholder}
                buttonText={buttonText}
                loading={loading}
                onQueryChange={setQuery}
                onSubmit={handleSubmit}
                onSuggestionSelect={handleSuggestionSelect}
                autocompleteApiUrl={autocompleteApiUrl}
                debounceMs={debounceMs}
            />            
            <div className="search-page-results-container">
                {error && (
                    <div className="search-error" role="alert">
                        <p>Error: {error}</p>
                    </div>
                )}

                {!error && (
                    <>
                        <SearchResultsHeader
                            totalResults={totalResults}
                            currentPage={currentPage}
                            pageSize={CLIENT_PAGE_SIZE}
                            resultsLength={results.length > 0 ? getPaginatedResults().length : 0}
                            sortBy={sortBy}
                            onSortChange={handleSortChange}
                        />
                        <div className="search-page-filters-and-results-container">
                            <SearchFilters 
                                selectedCategories={selectedCategories}
                                selectedPageTypes={selectedPageTypes}
                                onCategoryFilter={handleCategoryFilter}
                                onPageTypeFilter={handlePageTypeFilter}
                                onResetFilters={handleResetFilters}
                            />                                      
                            <div>
                                {hasSearched && results.length > 0 ? (
                                    <SearchResults
                                        results={getPaginatedResults()}
                                        loading={loading}
                                        totalResults={totalResults}
                                        currentPage={currentPage}
                                        pageSize={CLIENT_PAGE_SIZE}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                        onCategoryFilter={handleCategoryFilter}
                                        selectedCategories={selectedCategories}
                                    />
                                ) : (
                                    !loading && (
                                        <div className="rich-text">
                                            {hasSearched ? (
                                                <>
                                                    <p>No Results Found</p>
                                                    {(selectedCategories.length > 0 || selectedPageTypes.length > 0) && (
                                                        <p>Try adjusting your filters or <a onClick={handleResetFilters} style={{ cursor: 'pointer', textDecoration: 'underline' }}>reset all filters</a>.</p>
                                                    )}
                                                </>
                                            ) : (
                                                <div>
                                                    <p>Start by searching or applying filters to find what you're looking for.</p>
                                                </div>
                                            )}
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
